import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';

import KlassesIcon from './KlassesIcon';
import ScheduledLessonReport, {
  ScheduledLessonReportEnum,
} from '@/models/ScheduledLessonReport';
import LessonProgress from '@/models/LessonProgress';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ScheduledLesson from '@/models/ScheduledLesson';
import Skeleton from '@/components/ui/custom/Skeleton';
import VacationNoticeTag from '@/components/ui/custom/VacationNoticeTag';
import { VacationNotice } from '@/models/VacationNotice';
import { SimpleLessonProgress } from '@/models/SimpleLessonProgress';
import { presenceNotification } from '@/utils/lessonProgressNotifications';

interface KlassesIconCellProps {
  studentLessonProgress?: LessonProgress;
  disabled: boolean;
  testId?: string;
  scheduledLesson: ScheduledLesson;
  loading?: boolean;
  vacationNotice?: VacationNotice;
  progress?: SimpleLessonProgress;
  report?: ScheduledLessonReport;
}

export default function KlassesIconCell(props: KlassesIconCellProps) {
  const {
    disabled,
    scheduledLesson,
    loading,
    vacationNotice,
    progress,
    report,
  } = props;

  const { t } = useTranslation();

  const className = 'w-72 h-20 border-b border-neutral-content p-3.5';

  if (loading) {
    return (
      <Skeleton className={twJoin('bg-primary-content shrink-0', className)} />
    );
  }

  if (!progress) {
    return (
      <span className="flex text-center h-fit">
        <Text
          className={twJoin(
            'text-base-content/50 text-14 flex text-center items-center justify-center',
            className,
          )}
          text={t('klassProgressPage.noProgress')}
        />
      </span>
    );
  }

  return (
    <ProgressCell
      scheduledLesson={scheduledLesson}
      progress={progress}
      report={report}
      vacationNotice={vacationNotice}
      disabled={disabled}
    />
  );
}

type ProgressCellProps = {
  scheduledLesson: ScheduledLesson;
  progress: SimpleLessonProgress;
  disabled?: boolean;
  report?: ScheduledLessonReport;
  vacationNotice?: VacationNotice;
};

function ProgressCell(props: ProgressCellProps) {
  const { scheduledLesson, progress, report, vacationNotice, disabled } = props;

  const { t } = useTranslation();

  const [vacationNoticeRef, setVacationNoticeRef] =
    useState<HTMLElement | null>(null);

  const className = 'w-72 h-20 border-b border-neutral-content p-3.5';

  const isTransfer = report?.status === ScheduledLessonReportEnum.TRANSFERED;

  const presenceNotificationCount = presenceNotification(
    progress.presenceStatus,
  );

  return (
    <li
      data-testid={`studentIconCell${scheduledLesson.order}`}
      className={twJoin(
        'flex shrink-0 relative gap-6 justify-center items-center',
        className,
        disabled ? 'grayscale-[20] opacity-30' : '',
      )}
    >
      <ConditionalRenderer condition={isTransfer}>
        <div className="absolute top-0 h-full w-3 rounded-r-md left-0 bg-primary-content text-12">
          <Text
            className="rotate-90 mt-2"
            text={t('klassProgressPage.transferred')}
          />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={scheduledLesson.lesson.book}>
        <KlassesIcon
          notificationCount={Math.ceil(progress.bookProgressPercentage * 100)}
          type="studentBook"
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={scheduledLesson.lesson.activities.length}>
        <KlassesIcon
          notificationCount={progress.pendingActivitiesCount || 0}
          type="activity"
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={scheduledLesson.homeworkActivity}>
        <KlassesIcon
          notificationCount={progress.pendingHomework ? 1 : 0}
          type="homework"
        />
      </ConditionalRenderer>

      <KlassesIcon
        setRef={setVacationNoticeRef}
        disabled={presenceNotificationCount === -1}
        notificationCount={presenceNotificationCount}
        type="attendance"
      />

      <VacationNoticeTag
        className={{
          base: 'z-50 py-0.5 px-1 text-14 lowercase',
        }}
        offset={[0, -30]}
        target={vacationNoticeRef}
        render={!!vacationNotice}
      />
    </li>
  );
}
